export type Platform = 'polymarket' | 'kalshi' | 'opinionlabs' | 'predictfun' | 'limitless'
export const ALL_PLATFORMS = [
	'polymarket',
	'kalshi',
	'opinionlabs',
	'predictfun',
	'limitless',
] as const satisfies readonly Platform[]
export const DEFAULT_PLATFORMS: Platform[] = ['kalshi', 'polymarket', 'opinionlabs', 'predictfun']

// Unified market status
// - active: Market is open for trading
// - closed: Market is not active (closed, resolved, paused, etc.)
export type MarketStatus = 'active' | 'closed'
export type Category =
	| 'politics'
	| 'sports'
	| 'crypto'
	| 'economy'
	| 'entertainment'
	| 'science'
	| 'other'
export const ALL_CATEGORIES = [
	'politics',
	'sports',
	'crypto',
	'economy',
	'entertainment',
	'science',
	'other',
] as const satisfies readonly Category[]

// Event list item (shown in sidebar, table, and tiles)
export type EventListItem = {
	id: string
	factoryId?: string
	platform: Platform
	title: string
	rules?: string
	subtitle?: string
	imageUrl?: string
	category: Category
	endDate?: number // Unix time
	volume: number // USD aggregated
	volume24h: number // USD last 24h
	liquidity: number // USD
	marketCount: number // number of markets in event
	// Markets for tile view (sorted by volume, top N)
	markets?: Array<{
		id: string
		title: string
		yesPrice: number
		status?: MarketStatus
	}>
	// True if any market in this event is not tradable onchain
	hasUntradable?: boolean
	// True if ALL markets in this event are not active (closed/resolved)
	isInactive?: boolean
	// Winner name for resolved events (e.g. team name or outcome)
	winner?: string
}

// Event detail (shown in main panel)
export type EventDetail = Omit<EventListItem, 'markets'> & {
	markets: MarketInEvent[]
	settlementSources: Array<{ name: string; url: string }>
	seriesTicker: string
}

// Market within an event
export type MarketInEvent = {
	id: string
	ticker: string
	title: string
	subtitle: string
	yesPrice: number
	noPrice: number
	yesBid: number | null
	yesAsk: number | null
	noBid: number | null
	noAsk: number | null
	volume: number
	openInterest: number
	closeTime: number
	status: MarketStatus
	// Whether onchain trading is available
	// - Kalshi: false if market accounts are not initialized (redirect to kalshi.com)
	// - Polymarket: always true (all markets tradable onchain)
	isTradable: boolean
	// Whether this is a new/featured market (Polymarket only)
	isNew?: boolean
	// Whether this market is archived (Polymarket only - hidden from main feeds)
	isArchived?: boolean
	result?: string
	tradingData:
		| KalshiTradingData
		| PolymarketTradingData
		| OpinionLabsTradingData
		| PredictFunTradingData
}

// Individual market for flat list view
export type MarketListItem = {
	id: string
	platform: Platform
	title: string
	subtitle?: string
	imageUrl?: string
	category: Category
	yesPrice: number // 0-1 scale
	noPrice: number // 0-1 scale
	volume: number // USD
	volume24h: number // USD last 24h
	liquidity: number // USD
	openInterest: number // USD
	closeTime: number // unix ms
	status: MarketStatus
	// Whether onchain trading is available
	// - Kalshi: false if market accounts are not initialized (redirect to kalshi.com)
	// - Polymarket: always true (all markets tradable onchain)
	isTradable: boolean
	// Whether this is a new/featured market (Polymarket only)
	isNew?: boolean
	// Whether this market is archived (Polymarket only - hidden from main feeds)
	isArchived?: boolean
	eventId: string // parent event id
	eventTitle: string // parent event title
	parentEvent?: {
		parentEventSlug: string | null
		parentEventId: string | null
	}
}

export type KalshiTradingData = {
	platform: 'kalshi'
	ticker: string
	eventTicker: string
	accounts: Record<
		string,
		{
			marketLedger: string
			yesMint: string
			noMint: string
		}
	>
}

export type PolymarketTradingData = {
	platform: 'polymarket'
	conditionId: string
	clobTokenIds: [string, string] // [yes, no]
	marketMakerAddress: string
	negRisk?: boolean // Whether this market uses negRisk contracts
}

export type OpinionLabsTradingData = {
	platform: 'opinionlabs'
	topicId: number
	questionId: string
	chainId: string
	currencyAddress: string
	yesPos: string // Token ID for YES position (used for price history API)
	noPos: string // Token ID for NO position
}

export type PredictFunTradingData = {
	platform: 'predictfun'
	marketId: number
	conditionId: string
	outcomeIds: [string, string] // [yes, no] onChainIds
}
